/* eslint-disable */
/**
 * Reusable project/website selector for AI features.
 * Fetches user's projects and displays them in a user-friendly dropdown.
 * Each AI feature is scoped to the selected project (website).
 */
import { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import { getwebSiteList } from "../../gql/queries/userWebSiteList";

export default function ProjectSelector({
  value,
  onChange,
  label = "Select website",
  placeholder = "Choose a project to use AI features",
  required = false,
  disabled = false,
  size = "medium",
  fullWidth = true,
  showWebsiteInOption = true,
  sx = {},
}) {
  const theme = useTheme();
  const userData = useSelector(state => state.reducer.userData);
  const [projects, setProjects] = useState([]);
  const [fetchProjects, { loading }] = useLazyQuery(getwebSiteList, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (userData?.id) {
      fetchProjects({
        variables: { params: userData.id },
        onCompleted: data => {
          const list = data?.userWebVisitList || [];
          setProjects(Array.isArray(list) ? list : []);
        },
      });
    }
  }, [userData?.id]);

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      required={required}
      disabled={disabled || loading}
      sx={{ minWidth: 200, ...sx }}
    >
      {label ? <InputLabel id="project-selector-label">{label}</InputLabel> : null}
      <Select
        labelId={label ? "project-selector-label" : undefined}
        value={value || ""}
        label={label || undefined}
        onChange={e => onChange(e.target.value)}
        renderValue={selected => {
          if (!selected) return <span style={{ color: theme.palette.text.secondary }}>{placeholder}</span>;
          const p = projects.find(pr => pr.id === selected);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PublicIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {p?.projectName || p?.website || selected}
                </Typography>
                {showWebsiteInOption && p?.website && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {p.website}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
      >
        <MenuItem value="">
          <em>{placeholder}</em>
        </MenuItem>
        {projects.map(p => (
          <MenuItem key={p.id} value={p.id}>
            <Box sx={{ display: "flex", flexDirection: "column", py: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {p.projectName || p.website || p.id}
              </Typography>
              {p.website && (
                <Typography variant="caption" color="text.secondary">
                  {p.website}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
      {loading && (
        <CircularProgress
          size={20}
          sx={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />
      )}
    </FormControl>
  );
}
